"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail } from "lucide-react";
import Link from "next/link";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (res.status === 429) {
        setError("Muitas tentativas. Tente novamente em 1 hora.");
        return;
      }

      setSent(true);
    } catch {
      setError("Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-[400px] bg-card border border-border rounded-[12px] p-8">
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Voltar ao login
        </Link>

        <h1 className="text-[18px] font-bold text-foreground mb-1">Recuperar senha</h1>
        <p className="text-[13px] text-muted-foreground mb-6">
          Informe seu email e enviaremos um link para redefinir sua senha.
        </p>

        {sent ? (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-[8px] p-4">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-emerald-500" />
              <span className="text-[13px] font-semibold text-emerald-600">Email enviado</span>
            </div>
            <p className="text-[12px] text-muted-foreground">
              Se o email existir em nossa base, você receberá um link de recuperação. Verifique sua caixa de entrada e spam.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="bg-input focus:bg-card focus:border-accent"
              />
            </div>

            {error && (
              <p className="text-[12px] text-red-500 font-medium">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full min-h-[44px] sm:min-h-[36px] rounded-[8px] text-[13px] font-semibold"
            >
              {loading ? "Enviando..." : "Enviar link de recuperação"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
